// Light, dark, or as the device says. A device preference, like the client's device/writing.ts: a
// learner may want dark on the phone at night and light on the laptop. Applied
// before the first render (frontend.tsx), so the app never flashes the other.
import { loadTheme, storeTheme, type ThemeChoice } from "@aybuben/client/device/theme-choice.ts";
import { COL, DARK, themeCss } from "@aybuben/core/brand/palette.ts";

const darkQuery = () => globalThis.matchMedia?.("(prefers-color-scheme: dark)");

/** Whether the page is dark now: chosen, or the device's. */
export const isDark = (choice: ThemeChoice): boolean =>
	choice === "dark" || (choice === "system" && (darkQuery()?.matches ?? false));

/** Marks the root with the choice and colours the browser's own chrome to match. */
export function applyTheme(choice: ThemeChoice): void {
	const root = document.documentElement;
	if (choice === "system") delete root.dataset.theme;
	else root.dataset.theme = choice;
	document
		.querySelector('meta[name="theme-color"]')
		?.setAttribute("content", isDark(choice) ? DARK.bg : COL.bg);
}

export function saveTheme(choice: ThemeChoice): void {
	applyTheme(choice);
	storeTheme(choice);
}

/**
 * Both palettes as CSS variables, the remembered choice applied, and the
 * browser's chrome following the device while the choice is "system".
 */
export function installTheme(): void {
	const style = document.createElement("style");
	style.dataset.theme = "palette";
	style.textContent = themeCss();
	document.head.append(style);
	applyTheme(loadTheme());
	darkQuery()?.addEventListener("change", () => applyTheme(loadTheme()));
}
